import * as _ from 'underscore';

import { Module } from '../Module';

/**
* Directive that applies event handling to child elements that match the "tb-child-selector"
*
* Attributes:
*
*   tb-child-selector:              Apply event listening on matching child elements
*   tb-child-click:                 Click event handler with matching child passed as first arg
*   tb-child-mouseenter-touchstart: Mouseenter and Touchstart event handler with matching child passed as first arg
*   tb-child-mouseleave-touchmove:  Mouseleave and Touchmove event handler with matching child passed as first arg
*   tb-child-mousedown:             Mousedown event handler with matching child passed as first arg
*   tb-child-mouseup:               Mouseup event handler with matching child passed as first arg
*   tb-child-touchend:              Touchend event handler with matching child passed as first arg
*   tb-child-exclusion-selector:    Matching targets within the matching child elements that will not trigger events
*   tb-child-exclusion-events:      Space-separated list of events that the exclusion selector applies to
*
* Example usage:
*
* span.tb-my-parent-element(
*   tb-child-selector=".tb-my-child-class-to-listen-on",
*   tb-child-click="myClickFn(child)",
*   tb-child-mouseenter-touchstart="myMouseenterFn(child)",
*   tb-child-mouseleave-touchmove="myMouseleaveFn(child)",
*   tb-child-mousedown="myMousedownFn(child)",
*   tb-child-mouseup="myMouseupFn(child)",
*   tb-child-exclusion-selector=".tb-my-prevent-events-target-class",
*   tb-child-exclusion-events="click mousedown")
*/

interface IChildEventAttributes extends ng.IAttributes {
  tbChildSelector: string;
  tbChildClick?: string;
  tbChildMousedown?: string;
  tbChildMouseenterTouchstart?: string;
  tbChildMouseleaveTouchmove?: string;
  tbChildMouseup?: string;
  tbChildTouchend?: string;
  // Prevent events
  tbChildExclusionSelector?: string;
  tbChildExclusionEvents?: string;
}

interface IEventDirective {
  attrName: string;
  eventName: string;
  digest: boolean;
}

const directives: IEventDirective[] = [
  { attrName: 'tbChildClick',                 eventName: 'click',      digest: true  },
  { attrName: 'tbChildMousedown',             eventName: 'mousedown',  digest: true  },
  { attrName: 'tbChildMouseenterTouchstart',  eventName: 'mouseenter touchstart', digest: false },
  { attrName: 'tbChildMouseleaveTouchmove',   eventName: 'mouseleave touchmove', digest: false },
  { attrName: 'tbChildMouseup',               eventName: 'mouseup',    digest: true  },
  // Touch
  { attrName: 'tbChildTouchend',              eventName: 'touchend',   digest: true  }
];

_.each(directives, (directive: IEventDirective) => {
  Module.instance().directive(directive.attrName, <any[]>[
    '$parse',
    ($parse: ng.IParseService) => {
      return {
        link: (scope: ng.IScope, element: JQuery, attributes: IChildEventAttributes) => {
          const fn = $parse(attributes[directive.attrName]);
          const selector = attributes.tbChildSelector;
          const exclusionEvents: string[] = (attributes.tbChildExclusionEvents || '').split(' ');
          const exclusionSelector = _.contains(exclusionEvents, directive.eventName) ? attributes.tbChildExclusionSelector : '';
          const digest = directive.digest;
          const callFn = (event: JQuery.TriggeredEvent) => {
            fn(scope, { getChildFn: () => $(event.target).closest(selector), $event: event });
          };
          element.on(directive.eventName, selector, (event: JQuery.TriggeredEvent) => {
            if (exclusionSelector && $(event.target).filter(exclusionSelector).length) {
              return;
            }

            if (digest) {
              scope.$apply(() => {
                callFn(event);
              });
            } else {
              callFn(event);
            }
          });
        }
      };
    }
  ]);
});
